import * as React from 'react';
import { useTheme } from '@emotion/react';
import { motion } from 'motion/react';
import { STORY_SCENES, STORY_CONNECTIONS, STORY_SHARED_CONNECTIONS, StoryScene, STORY_TIMELINE } from '../story';
import { S } from '../styles';
import { TaskConnectionWidget } from '../../../TaskConnectionWidget';
import { AppFleetArtwork } from './AppFleetArtwork';
import { AppConnectionsArtwork } from './AppConnectionsArtwork';
import { PlatformSystemsArtwork } from './PlatformSystemsArtwork';
import { CloudIntegrationsArtwork } from './CloudIntegrationsArtwork';
import { TransformationArtwork } from './TransformationArtwork';
import { KeepBuildingArtwork } from './KeepBuildingArtwork';
import { PrototypeScene } from './PrototypeScene';
import { OxideAppWorkspace, OxideCloudCodeWorkspace } from './OxideWorkspace';
import { BrowserInspection } from './BrowserInspection';
import { FieldInspectionArtwork } from './FieldInspectionArtwork';

interface StoryWorldProps {
  scene: StoryScene;
  elapsed: number;
  showingIntro: boolean;
  reducedMotion: boolean;
  animateConnections: boolean;
}

export const StoryWorld: React.FC<StoryWorldProps> = ({
  scene,
  elapsed,
  showingIntro,
  reducedMotion,
  animateConnections
}) => {
  const theme = useTheme();
  const active = STORY_SCENES[scene];
  const gridId = React.useId();
  const arrowId = React.useId();
  const reveal = (firstScene: StoryScene) => ({ opacity: scene >= firstScene ? 1 : 0 });
  const transition = { duration: reducedMotion ? 0 : 0.7 };
  const colors = theme.featureGraph;
  const finale = scene === StoryScene.KEEP_BUILDING;
  const simplifyProgress = finale ? (reducedMotion ? 1 : Math.min(1, elapsed / STORY_TIMELINE.finale.simplifyMs)) : 0;
  const simplify = simplifyProgress * simplifyProgress * (3 - 2 * simplifyProgress);
  const grouped = scene >= StoryScene.MULTIPLE_APPS;
  const focusApps = scene === StoryScene.MULTIPLE_APPS;
  const intro = scene === StoryScene.APP;
  const connected = scene === StoryScene.CONNECTED;
  const connectedTime = reducedMotion ? STORY_SCENES[StoryScene.CONNECTED].durationMs : elapsed;
  const connectedProgress = (start: number, duration: number) =>
    Math.max(0, Math.min(1, (connectedTime - start) / duration));
  const connectedTiming = STORY_TIMELINE.connected;
  const workspaceTiming = connectedTiming.workspace;
  const workspaceVisible =
    connected && connectedTime >= workspaceTiming.startMs && connectedTime < workspaceTiming.endMs;
  const workflowVisible = connected && connectedTime >= connectedTiming.workflowStartMs;
  const taskTriggered = !showingIntro && (scene > StoryScene.CONNECTED || workflowVisible);
  const cloudOffsetTiming = connectedTiming.cloudOffset;
  const cloudOffset =
    connected && connectedTime >= cloudOffsetTiming.startMs && connectedTime < cloudOffsetTiming.endMs
      ? cloudOffsetTiming.shifted
      : connected
        ? cloudOffsetTiming.default
        : 0;
  const cameraTiming = connectedTiming.camera;
  const connectedCamera =
    connectedTime < cameraTiming.closeUntilMs
      ? '-20 180 740 500'
      : connectedTime < cameraTiming.wideUntilMs
        ? '-20 0 1380 810'
        : '-20 170 1380 510';
  const webApp = scene >= StoryScene.ORGANIZATION;
  const introTime = showingIntro ? 0 : reducedMotion || !intro ? STORY_SCENES[StoryScene.APP].durationMs : elapsed;
  const progress = (start: number, duration: number) => Math.max(0, Math.min(1, (introTime - start) / duration));
  const prototypeTiming = STORY_TIMELINE.prototype;
  const sketch = progress(prototypeTiming.sketch.startMs, prototypeTiming.sketch.durationMs);
  const build = progress(prototypeTiming.build.startMs, prototypeTiming.build.durationMs);
  const live = progress(prototypeTiming.live.startMs, prototypeTiming.live.durationMs);
  const ease = (value: number) => value * value * (3 - 2 * value);
  const handoff = ease(progress(prototypeTiming.handoff.startMs, prototypeTiming.handoff.durationMs));
  const download = progress(prototypeTiming.download.startMs, prototypeTiming.download.durationMs);
  const downloadProgress = progress(
    prototypeTiming.downloadProgress.startMs,
    prototypeTiming.downloadProgress.durationMs
  );
  const run = progress(prototypeTiming.run.startMs, prototypeTiming.run.durationMs);
  const loadApp = ease(progress(prototypeTiming.loadApp.startMs, prototypeTiming.loadApp.durationMs));
  const connection = progress(prototypeTiming.live.startMs, prototypeTiming.live.durationMs);
  const ready = progress(prototypeTiming.ready.startMs, prototypeTiming.ready.durationMs);
  const completePicture = ease(
    progress(prototypeTiming.completePicture.startMs, prototypeTiming.completePicture.durationMs)
  );
  const appOffset = intro ? -345 * handoff * (1 - loadApp) : 0;
  const camera = prototypeTiming.camera;
  const prototypeCamera =
    introTime < camera.sketchUntilMs
      ? '-335 200 980 470'
      : introTime < camera.handoffUntilMs
        ? '-550 200 870 470'
        : introTime < camera.runtimeUntilMs
          ? '-390 20 1190 750'
          : '-620 20 1420 750';

  return (
    <S.World
      role="img"
      aria-hidden={showingIntro}
      aria-label={`${active.heading} ${active.caption}`}
      initial={false}
      animate={{ viewBox: intro ? prototypeCamera : connected ? connectedCamera : active.camera }}
      transition={{ duration: reducedMotion ? 0 : 1.6, ease: [0.22, 1, 0.36, 1] }}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <marker
          id={arrowId}
          viewBox="0 0 8 8"
          refX="8"
          refY="4"
          markerWidth="8"
          markerHeight="8"
          markerUnits="userSpaceOnUse"
          orient="auto-start-reverse"
        >
          <path d="M1 1 L7 4 L1 7" fill="none" stroke={colors.cloudService.accent} strokeWidth="1.5" />
        </marker>
        <pattern id={gridId} width="32" height="32" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="1" fill={theme.surface.border} />
        </pattern>
      </defs>
      <rect x="-2000" y="-1000" width="5000" height="3000" fill={`url(#${gridId})`} />
      <motion.g initial={false} animate={{ opacity: showingIntro ? 0 : 1 }} transition={transition}>
        <g
          transform={`translate(${360 * simplify} ${-75 * simplify}) scale(${1 - 0.8 * simplify})`}
          opacity={1 - simplify}
        >
          <TransformationArtwork scene={scene} reducedMotion={reducedMotion} />
          <AppFleetArtwork
            scene={scene}
            grouped={grouped}
            reducedMotion={reducedMotion}
            animateConnections={animateConnections}
          />
          <AppConnectionsArtwork
            scene={scene}
            focusApps={focusApps}
            reducedMotion={reducedMotion}
            animateConnections={animateConnections}
          />
          <g
            style={{
              transform:
                scene >= StoryScene.MULTIPLE_APPS
                  ? 'translate(64px, 390px) scale(0.25)'
                  : 'translate(0px, 0px) scale(1)',
              transformOrigin: '0px 0px',
              transformBox: 'view-box',
              transition: reducedMotion ? 'none' : 'transform 1.6s cubic-bezier(0.22, 1, 0.36, 1)'
            }}
          >
            <motion.g initial={false} animate={{ opacity: intro ? 1 : 0 }} transition={transition}>
              <PrototypeScene
                animateConnections={animateConnections}
                live={live}
                handoff={handoff}
                completePicture={completePicture}
                loadApp={loadApp}
                download={download}
                downloadProgress={downloadProgress}
                run={run}
                connection={connection}
                ready={ready}
              />
            </motion.g>
            {STORY_CONNECTIONS.map((connection) => {
              const path =
                connection.id === 'sync'
                  ? connected
                    ? `M265 440 C321 440 321 ${410 + cloudOffset} 377 ${410 + cloudOffset}`
                    : webApp
                      ? 'M335 410 H400'
                      : connection.path
                  : connected && connection.id === 'event'
                    ? 'M627 410 H739'
                    : connected && connection.id === 'report'
                      ? 'M989 410 H1025 Q1045 410 1045 390 V330 Q1045 310 1065 310 H1101'
                      : connected && connection.id === 'external'
                        ? 'M989 410 H1025 Q1045 410 1045 430 V500 Q1045 520 1065 520 H1101'
                        : connection.path;
              const connectionColor =
                connection.id === 'oxide'
                  ? colors.interface
                  : connection.id === 'cli' || connection.id === 'ai-coding'
                    ? colors.tool
                    : connection.id === 'browser'
                      ? colors.runtime
                      : colors.cloudService;
              return (
                <motion.g
                  key={connection.id}
                  initial={false}
                  animate={{
                    y: connection.id === 'sync' ? 0 : cloudOffset,
                    opacity:
                      grouped && ['oxide', 'sector', 'override', 'cli', 'ai-coding'].includes(connection.id)
                        ? 0
                        : connected && connection.scene === StoryScene.CONNECTED
                          ? (
                              connection.id === 'sync'
                                ? connectedTime >= connectedTiming.connectionStartMs
                                : workflowVisible
                            )
                            ? 1
                            : 0
                          : scene >= connection.scene
                            ? 1
                            : 0
                  }}
                  transition={transition}
                >
                  <path
                    transform={connection.id === 'sync' ? 'translate(0 -8)' : undefined}
                    d={path}
                    fill="none"
                    stroke={
                      ['oxide', 'cli', 'browser', 'ai-coding'].includes(connection.id)
                        ? connectionColor.accent
                        : connectionColor.border
                    }
                    strokeWidth="2"
                    markerEnd={
                      ['oxide', 'cli', 'browser', 'ai-coding'].includes(connection.id) ? undefined : `url(#${arrowId})`
                    }
                  />
                  {connection.id === 'sync' && (
                    <path
                      d={path}
                      transform="translate(0 8)"
                      fill="none"
                      stroke={connectionColor.border}
                      strokeWidth="2"
                      markerStart={`url(#${arrowId})`}
                    />
                  )}
                  {connection.id === 'event' ? (
                    <S.TriggerPulse
                      d={path}
                      active={taskTriggered}
                      running={animateConnections}
                      fill="none"
                      stroke={connectionColor.accent}
                      strokeWidth="3"
                      strokeLinecap="round"
                      pathLength="1"
                      strokeDasharray="0.14 0.86"
                    />
                  ) : ['report', 'external'].includes(connection.id) ? (
                    <TaskConnectionWidget
                      d={path}
                      active={taskTriggered}
                      running={animateConnections}
                      color={connectionColor.accent}
                    />
                  ) : (
                    <S.Flow
                      transform={connection.id === 'sync' ? 'translate(0 -8)' : undefined}
                      d={path}
                      running={animateConnections && !['oxide', 'cli', 'browser', 'ai-coding'].includes(connection.id)}
                      fill="none"
                      stroke={connectionColor.accent}
                      strokeWidth="2"
                      strokeDasharray="5 22"
                    />
                  )}
                  {connection.id === 'sync' && (
                    <S.Flow
                      d={path}
                      transform="translate(0 8)"
                      running={animateConnections}
                      style={{ animationDirection: 'reverse' }}
                      fill="none"
                      stroke={connectionColor.accent}
                      strokeWidth="2"
                      strokeDasharray="5 22"
                    />
                  )}
                  {connection.label ? (
                    <text
                      x={
                        connected && connection.id === 'sync'
                          ? 321
                          : connected && connection.id === 'event'
                            ? 683
                            : connection.id === 'sync' && webApp
                              ? 367
                              : connection.x
                      }
                      y={connection.id === 'sync' ? (connected ? 418 : connection.y - 4) : connection.y}
                      textAnchor={connection.id === 'oxide' ? 'end' : connection.id === 'cli' ? 'start' : 'middle'}
                      fill={connectionColor.accent}
                      fontSize={connection.id === 'ai-coding' ? 9 : 10}
                      letterSpacing={connection.id === 'ai-coding' ? 0.3 : 2}
                    >
                      {connection.label}
                    </text>
                  ) : null}
                </motion.g>
              );
            })}

            <FieldInspectionArtwork
              scene={scene}
              appOffset={appOffset}
              sketch={sketch}
              live={live}
              ready={ready}
              reducedMotion={reducedMotion}
            />
            <motion.g initial={false} animate={{ opacity: webApp ? 1 : 0 }} transition={transition}>
              <BrowserInspection />
            </motion.g>
            <motion.g
              initial={false}
              animate={{
                opacity: connected ? (workflowVisible ? 1 : 0) : reveal(StoryScene.CONNECTED).opacity,
                y: cloudOffset
              }}
              transition={transition}
            >
              {[
                { y: 260, title: 'A report, ready.', detail: 'Generate & deliver a PDF', symbol: '↗' },
                { y: 470, title: 'Your existing systems', detail: 'Exchange data through APIs', symbol: '⇄' }
              ].map((item) => (
                <g key={item.title} transform={`translate(${connected ? 1101 : 1100} ${item.y})`}>
                  <rect
                    width="220"
                    height="105"
                    rx="16"
                    fill={theme.surface.background}
                    stroke={colors.feature.border}
                    strokeWidth="1.5"
                  />
                  <text x="18" y="30" fill={colors.feature.accent} fontSize="20">
                    {item.symbol}
                  </text>
                  <text x="18" y="58" fill={theme.text.heading} fontSize="16" fontWeight="500">
                    {item.title}
                  </text>
                  <text x="18" y="80" fill={theme.text.paragraph} fontSize="11">
                    {item.detail}
                  </text>
                </g>
              ))}
            </motion.g>
          </g>
          <motion.g
            initial={false}
            animate={{ opacity: focusApps ? 0 : scene >= StoryScene.ORGANIZATION ? 1 : 0 }}
            transition={transition}
          >
            <text x="1100" y="685" fill={colors.feature.accent} fontSize="10" letterSpacing="2">
              ONE CONNECTED PLATFORM
            </text>
            <text x="1100" y="720" fill={theme.text.heading} fontSize="22" fontWeight="600">
              More teams.
            </text>
            <text x="1100" y="750" fill={theme.text.heading} fontSize="22" fontWeight="600">
              More possibilities.
            </text>
            <text x="1100" y="779" fill={theme.text.paragraph} fontSize="12">
              The same foundation.
            </text>
          </motion.g>
          <motion.g initial={false} animate={{ opacity: focusApps ? 0 : grouped ? 1 : 0 }} transition={transition}>
            {STORY_SHARED_CONNECTIONS.map((path) => (
              <g key={path}>
                <path d={path} fill="none" stroke={colors.interface.border} strokeWidth="2" />
                <S.Flow
                  d={path}
                  running={animateConnections}
                  fill="none"
                  stroke={colors.interface.accent}
                  strokeWidth="2"
                  strokeDasharray="5 22"
                />
              </g>
            ))}
          </motion.g>
          <motion.g
            initial={false}
            animate={{ opacity: intro || workspaceVisible ? 1 : 0, x: intro ? -500 : 400, y: intro ? 265 : 50 }}
            transition={{ duration: reducedMotion ? 0 : 1.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <g
              opacity={build * (1 - (intro ? handoff * (1 - completePicture) : 0))}
              transform={`translate(${-24 * (1 - build) - (intro ? 70 * completePicture : 0)} ${intro ? -30 * completePicture : 0})`}
            >
              {intro ? (
                <OxideAppWorkspace build={build} live={live} />
              ) : (
                <OxideCloudCodeWorkspace
                  build={
                    connected
                      ? connectedProgress(
                          connectedTiming.workspaceProgress.buildStartMs,
                          connectedTiming.workspaceProgress.buildDurationMs
                        )
                      : build
                  }
                  live={
                    connected
                      ? connectedProgress(
                          connectedTiming.workspaceProgress.liveStartMs,
                          connectedTiming.workspaceProgress.liveDurationMs
                        )
                      : live
                  }
                />
              )}
            </g>
          </motion.g>
          <motion.g
            initial={false}
            animate={{ opacity: workspaceVisible && connectedTime >= workspaceTiming.readyMs ? 1 : 0 }}
            transition={transition}
          >
            <path
              d="M790 200 H844 Q864 200 864 220 V418 M858 410 L864 418 L870 410"
              fill="none"
              stroke={colors.interface.accent}
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <text x="881" y="309" fill={colors.interface.accent} fontSize="10" letterSpacing="1">
              BUILD &amp; DEPLOY
            </text>
          </motion.g>
          <PlatformSystemsArtwork
            scene={scene}
            connectedTime={connectedTime}
            focusApps={focusApps}
            grouped={grouped}
            cloudOffset={cloudOffset}
            reducedMotion={reducedMotion}
            taskTriggered={taskTriggered}
            animateConnections={animateConnections}
          />
          <CloudIntegrationsArtwork
            scene={scene}
            reducedMotion={reducedMotion}
            animateConnections={animateConnections}
          />
        </g>
        {finale && <KeepBuildingArtwork elapsed={elapsed} reducedMotion={reducedMotion} running={animateConnections} />}
      </motion.g>
    </S.World>
  );
};
